// types and consts
import { LEGAL_DISCLAIMER_OPTIONS } from '@components/EmailSignaturev2/consts';
import {
  type EmailSignatureBasicSelectOption,
  EmailSignatureSizes,
  SideEditorNodeKey,
} from '@components/EmailSignaturev2/types';
import { FormSelector, FormSelectorGroup } from '@components/FormSelector/FormSelector';
import Input from '@components/Input/Input';
import { SideEditorScrollType } from '@components/SideEditor/SideEditor';
// hooks and context
import { useSideEditorContext } from '@components/SideEditor/utils';
import Tooltip from '@components/Tooltip/Tooltip';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import { faQuestionCircle, faTrash } from '@fortawesome/pro-light-svg-icons';
import { faBalanceScale } from '@fortawesome/pro-regular-svg-icons';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
// components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNode from '@hooks/use-node';
// utils
import { generateNewLegalDisclaimerId } from '@modules/email-signatures-helpers';
import Emitter from '@modules/event-emitter';
import { debounce } from '@modules/helpers';
import { type FC, useContext, useEffect, useRef, useState } from 'react';
import Select from 'react-select';

import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import {
  EmailSignatureEditorAddonsBox,
  EmailSignatureEditorAddonsBoxBody,
  EmailSignatureEditorAddonsBoxHeader,
  EmailSignatureEditorAddonsBoxHeaderContents,
  EmailSignatureEditorAddonsBoxHeaderDivider,
} from './EmailSignatureEditorAddonBox';
import styles from './EmailSignatureEditorAddons.module.scss';
import legalStyles from './EmailSignatureEditorAddonsLegalDisclaimerBox.module.scss';

const EmailSignatureEditorAddonsLegalDisclaimerBox: FC<React.PropsWithChildren<unknown>> = () => {
  const { scrollIntoView, registerNode, panelIndex } = useSideEditorContext();
  const { isInTransitionState, emailSignature, emailSignatureDispatch, isSignatureLocked } = useContext(
    emailSignatureSideEditorServicesContext,
  );
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement>();
  const [selectedDisclaimerOption, setSelectedFooterOption] =
    useState<EmailSignatureBasicSelectOption | null>(LEGAL_DISCLAIMER_OPTIONS[0]);
  const [legalDisclaimerBox, legalDisclaimerBoxRef] = useNode<HTMLDivElement>();
  const debouncedShowDummyLegalDisclaimer = useRef(
    debounce(() => {
      Emitter.emit('SHOW_DUMMY_LEGAL_DISCLAIMER');
    }, 200),
  );

  const debouncedRemoveDummyLegalDisclaimer = useRef(
    debounce(() => {
      Emitter.emit('REMOVE_DUMMY_LEGAL_DISCLAIMER');
    }, 200),
  );

  useEffect(() => setMenuPortalTarget(document.body), []);

  useEffect(() => {
    if (!legalDisclaimerBox) return;
    registerNode(SideEditorNodeKey.LEGAL_DISCLAIMER_BOX_NODE, {
      panelBelongTo: panelIndex,
      node: legalDisclaimerBox,
    });
  }, [legalDisclaimerBox, panelIndex, registerNode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    const handleScrollToSocialLinks = () => {
      if (isInTransitionState) {
        return;
      }
      scrollIntoView(SideEditorNodeKey.LEGAL_DISCLAIMER_BOX_NODE, SideEditorScrollType.START, false);
    };
    Emitter.on('SCROLL_TO_LEGAL_DISCLAIMER', handleScrollToSocialLinks);

    return () => {
      Emitter.off('SCROLL_TO_LEGAL_DISCLAIMER', handleScrollToSocialLinks);
    };
  }, [isInTransitionState, panelIndex, scrollIntoView]);

  const handleAddLegalDisclaimer = () => {
    emailSignatureDispatch({
      type: 'ADD_LEGAL_DISCLAIMER',
      value: {
        id: generateNewLegalDisclaimerId(),
        value: LEGAL_DISCLAIMER_OPTIONS[0].value,
        fontSize: EmailSignatureSizes.MEDIUM,
      },
    });
  };

  const handleRemoveLegalDisclaimer = () => {
    emailSignatureDispatch({
      type: 'DELETE_LEGAL_DISCLAIMER',
    });
    setSelectedFooterOption(LEGAL_DISCLAIMER_OPTIONS[0]);
  };

  const handleSelectedDisclaimerOptionChange = (
    selectedDisclaimerOption: EmailSignatureBasicSelectOption | null,
  ) => {
    emailSignatureDispatch({
      type: 'UPDATE_LEGAL_DISCLAIMER_CONTENT',
      value: selectedDisclaimerOption?.value ?? '',
    });
    setSelectedFooterOption(selectedDisclaimerOption);
  };

  const handleLegalDisclaimerValueChange = (newValue: string) => {
    emailSignatureDispatch({
      type: 'UPDATE_LEGAL_DISCLAIMER_CONTENT',
      value: newValue,
    });
    // set to CUSTOM option
    setSelectedFooterOption(LEGAL_DISCLAIMER_OPTIONS[LEGAL_DISCLAIMER_OPTIONS.length - 1]);
  };

  const handleLegalDisclaimerFontSizeChange = (newFontSize: string) => {
    emailSignatureDispatch({
      type: 'UPDATE_LEGAL_DISCLAIMER_FONT_SIZE',
      value: newFontSize as EmailSignatureSizes,
    });
  };

  return (
    <EmailSignatureEditorAddonsBox ref={legalDisclaimerBoxRef}>
      <EmailSignatureEditorAddonsBoxHeader>
        <EmailSignatureEditorAddonsBoxHeaderContents>
          <FontAwesomeIcon
            size="1x"
            icon={faBalanceScale}
            className={legalStyles['addons-box-header-legal-icon']}
          />
          <span>Legal disclaimer</span>
          {!emailSignature.legalDisclaimer && (
            <Tooltip
              label={
                'An email disclaimer is a statement, notice, or warning that is added to limit liability.'
              }
              aria-label={'addon-legal-info'}
            >
              <span>
                <button
                  onMouseEnter={() => debouncedShowDummyLegalDisclaimer.current()}
                  onMouseLeave={() => debouncedRemoveDummyLegalDisclaimer.current()}
                  onFocus={() => debouncedShowDummyLegalDisclaimer.current()}
                  onBlur={() => debouncedRemoveDummyLegalDisclaimer.current()}
                  className={styles['addons-box-header-info-button']}
                >
                  <FontAwesomeIcon size="1x" icon={faQuestionCircle} />
                </button>
              </span>
            </Tooltip>
          )}
        </EmailSignatureEditorAddonsBoxHeaderContents>
        {!emailSignature.legalDisclaimer && (
          <Tooltip
            size="small"
            label="You cannot add a legal disclaimer, please contact your admin if you have any questions"
            aria-label="lock explaination message"
            show={isSignatureLocked}
          >
            <button
              disabled={isSignatureLocked}
              onClick={handleAddLegalDisclaimer}
              className={styles['addons-box-header-add-button']}
              data-is-locked={isSignatureLocked}
            >
              {isSignatureLocked ? <FontAwesomeIcon icon={faLock} /> : 'Add'}
            </button>
          </Tooltip>
        )}

        {emailSignature.legalDisclaimer && !isSignatureLocked && (
          <button onClick={handleRemoveLegalDisclaimer} className={styles['addons-box-header-delete-button']}>
            <FontAwesomeIcon size="1x" icon={faTrash} />
          </button>
        )}
      </EmailSignatureEditorAddonsBoxHeader>
      {emailSignature.legalDisclaimer && <EmailSignatureEditorAddonsBoxHeaderDivider />}
      <EmailSignatureEditorAddonsBoxBody collapsed={!emailSignature.legalDisclaimer}>
        <div className={styles['addons-selector-textarea-field']}>
          <div className={styles['addons-selector-field-container']}>
            <div className={styles['addons-field-label']}>Disclaimer type</div>
            <Select
              //https://github.com/JedWatson/react-select/issues/2629
              id={'very long passage to fix the ssr bug in react-select'}
              instanceId={'very long passage to fix the ssr bug in react-select'}
              maxMenuHeight={200}
              components={{
                // biome-ignore lint/correctness/noNestedComponentDefinitions: historic
                IndicatorSeparator: () => null,
              }}
              styles={{
                container: () => ({ width: '100%' }),
                control: (base) => ({
                  ...base,
                  height: '2rem',
                  borderRadius: '5px',
                  width: '100%',
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              value={selectedDisclaimerOption}
              options={LEGAL_DISCLAIMER_OPTIONS}
              onChange={handleSelectedDisclaimerOptionChange}
              menuPortalTarget={menuPortalTarget}
              isDisabled={isSignatureLocked}
            />
          </div>
          <div className={legalStyles['addons-selector-legal-textarea']}>
            <Input
              hideAllErrorMessages
              multiLine
              value={emailSignature.legalDisclaimer?.value ?? ''}
              onChange={handleLegalDisclaimerValueChange}
              disabled={isSignatureLocked}
            />
          </div>
        </div>
        <div className={styles['addons-field']}>
          <div className={styles['addons-field-label']}>Font size</div>
          <FormSelectorGroup
            className={styles['addons-form-selector-group']}
            selectedValue={emailSignature.legalDisclaimer?.fontSize ?? EmailSignatureSizes.SMALL}
            size="sm"
            onChange={handleLegalDisclaimerFontSizeChange}
          >
            <div className={styles['addons-form-selector']}>
              <FormSelector disabled={isSignatureLocked} value={EmailSignatureSizes.SMALL}>
                Small
              </FormSelector>
            </div>
            <div className={styles['addons-form-selector']}>
              <FormSelector disabled={isSignatureLocked} value={EmailSignatureSizes.MEDIUM}>
                Medium
              </FormSelector>
            </div>
            <div className={styles['addons-form-selector']}>
              <FormSelector disabled={isSignatureLocked} value={EmailSignatureSizes.LARGE}>
                Large
              </FormSelector>
            </div>
          </FormSelectorGroup>
        </div>

        {isSignatureLocked && (
          <EmailSignatureLockLayer message="You cannot edit the legal disclaimer, please contact your admin if you have any questions" />
        )}
      </EmailSignatureEditorAddonsBoxBody>
    </EmailSignatureEditorAddonsBox>
  );
};

export default EmailSignatureEditorAddonsLegalDisclaimerBox;
